import React, {useCallback, useState} from "react";
import intl from "react-intl-universal";
import {toJS} from "mobx";
import {useAppState, useDsManager} from "@stores/AppStateContext";

import {MODALS} from "../modal-names";
import {TaskListUnit} from "../../../../stores/taskLists/gql/serverTypes";
import {TaskListUnitAvatar} from "../../../components/Avatar/TaskListUnitAvater";
import {InfoIcon} from "../../../components/icons/InfoIcon";
import {ModalConfirm} from "../../../components/ModalConfirm";
import {Select, SelectOption} from "../../../components/form/Select";
import {FormHint} from "../../../components/FormHint";
import {CalendarTable} from "../../../screens/Lists/components/CalendarTable";
import {copyTaskToUnit} from "../../../../actions/taskList/copyTaskToUnit";
import {TaskListUnitModel} from "../../../../stores/taskLists/TaskListUnitModel";

import "./CopyToForTaskModal.css";

export interface CopyToForTaskModalProps {
    calendarTable: CalendarTable;
    taskIds: string[];
}

export const CopyToForTaskModal = (
    {calendarTable, taskIds}: CopyToForTaskModalProps
) => {
    const [selectedUnit, setSelectedUnit] = useState<string | null>(null);
    const appState = useAppState();
    const dsManager = useDsManager();

    const handleClose = useCallback(() => {
        appState.setModal(null);
    }, []);

    const handleConfirm = useCallback(async (): Promise<void> => {
        if (!selectedUnit) return;
        const unitDs = dsManager!.getTaskListUnitsDataSource(calendarTable.wsId);
        const unit = unitDs.getTaskListUnitById(selectedUnit);
        if (!unit) return;

        const unitModel = TaskListUnitModel.create(toJS(unit), calendarTable.getTaskListUnitModelEnv());
        const taskModelEnv = calendarTable.getTaskModelEnv();
        for (const id of taskIds) {
            const task = calendarTable.taskDs?.getTaskById(id);
            if (task) {
                await copyTaskToUnit(task, unitModel, taskModelEnv);
            }
        }
        handleClose();
        calendarTable.removeCheckedTasks(taskIds);
    }, [selectedUnit, taskIds, calendarTable, handleClose]);

    const activeModal = appState.topModal;
    const unitDs = dsManager!.getTaskListUnitsDataSource(calendarTable.wsId);
    const branchOptions: SelectOption[] = unitDs.filteredRows.map((branch: TaskListUnit) => ({
        value: branch.id,
        textForFilter: branch.name,
        label: (
            <>
                <TaskListUnitAvatar className="CopyToForTaskModal__BranchOption" taskListUnit={branch} size={24}/>
                {branch.name}
            </>
        )
    }));

    return (
        <ModalConfirm
            onConfirm={handleConfirm}
            confirmText={intl.get('CopyToForTaskModal.confirm.button').d('Copy to Locations')}
            onCancel={handleClose}
            header={intl.get('CopyToForTaskModal.header').d('Copy Task to Other Locations')}
            open={activeModal === MODALS.COPY_TO_FOR_TASK}
            onClose={handleClose}
            subtitle={intl.get('CopyToForTaskModal.topText', { task: '' }).d('Select one or more locations to copy the task')}
        >
            <div>
                <Select
                    selectedValue={selectedUnit}
                    options={branchOptions}
                    placeholder={intl.get('CopyToForTaskModal.select_branches').d('Add locations')}
                    size="large"
                    onChange={(newVal) => setSelectedUnit(newVal)}
                    allowFilter={true}
                    isFullWidth={true}
                    withLabel={true}
                    withBorder={true}
                />
            </div>
            <FormHint
                className="CopyToForTaskModal__info"
                size="small"
                icon={<InfoIcon size={16}/>}
                text={intl.get('CopyToForTaskModal.info').d("The task will not be linked — changes in one location won't affect others.")}
            />
        </ModalConfirm>
    );
};
